import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, MessagesSquare, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

/** Painel Comunicação Twilio — abas SMS/WA/Email + histórico dos últimos 20 envios.
 *  Props:
 *   - commHistory: lista atualizada de comm_logs (do parent)
 *   - onReloadHistory: callback (ex. loadCommHistory)
 *   - heatmapFilter: {day, hour} opcional — drill-down do heatmap
 *   - onClearHeatmapFilter: callback pra limpar o filtro
 */
export const CommsPanel = ({ commHistory, onReloadHistory, heatmapFilter, onClearHeatmapFilter }) => {
  const [commsChan, setCommsChan] = useState("sms");
  const [smsForm, setSmsForm] = useState({ to: "", message: "" });
  const [smsBusy, setSmsBusy] = useState(false);
  const [waForm, setWaForm] = useState({ to: "", message: "", content_sid: "" });
  const [waBusy, setWaBusy] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "RAF Direct — Teste", html: "<p>Teste Twilio Email API</p>" });
  const [emailBusy, setEmailBusy] = useState(false);

  const sendTestSms = async (e) => {
    e.preventDefault(); setSmsBusy(true);
    try {
      const { data } = await api.post("/admin/sms/test", smsForm);
      if (data.ok) toast.success(`SMS enviado — SID ${data.sid}`);
      else toast.error(data.reason || "Falha ao enviar SMS");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Erro"); }
    finally { setSmsBusy(false); onReloadHistory && onReloadHistory(); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="admin-comms-panel">
      <div className="mb-4">
        <h2 className="font-display font-bold text-lg text-[#0B2B62] mb-1">Comunicação Twilio</h2>
        <p className="text-xs text-slate-500">Testes de entrega dos 3 canais operacionais RAF: SMS, WhatsApp Business, Email Comms API.</p>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[["sms","SMS"],["wa","WhatsApp"],["email","Email"]].map(([k,l]) => (
          <button key={k} onClick={() => setCommsChan(k)} data-testid={`comms-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${commsChan===k ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {commsChan === "sms" && (
        <form onSubmit={sendTestSms} className="grid gap-3 md:grid-cols-3">
          <input data-testid="sms-to" type="text" placeholder="(11) 99642-0785" required value={smsForm.to} onChange={(e) => setSmsForm({ ...smsForm, to: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input data-testid="sms-message" type="text" placeholder="Mensagem (opcional)" value={smsForm.message} onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm md:col-span-2" />
          <button type="submit" disabled={smsBusy} data-testid="sms-send" className="md:col-span-3 justify-self-start px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {smsBusy ? "Enviando…" : "Enviar SMS"}
          </button>
        </form>
      )}

      {commsChan === "wa" && (
        <form onSubmit={async (e) => {
          e.preventDefault(); setWaBusy(true);
          try {
            // Só envia content_sid se começar com "HX" (SID válido do Twilio).
            const payload = { to: waForm.to, message: waForm.message };
            if (waForm.content_sid && waForm.content_sid.trim().startsWith("HX")) {
              payload.content_sid = waForm.content_sid.trim();
            }
            const { data } = await api.post("/admin/whatsapp/test", payload);
            if (data.ok && data.channel === "twilio_business") toast.success(`WhatsApp enviado — ${data.sid}`);
            else if (data.whatsapp_link) toast.info("Twilio WA falhou — abrir wa.me manual?", {
              duration: 15000,
              action: { label: "Abrir wa.me", onClick: () => window.open(data.whatsapp_link, "_blank") },
            });
            else toast.error(data.reason || "Falha");
          } catch (e2) { toast.error(e2?.response?.data?.detail || "Erro"); }
          finally { setWaBusy(false); onReloadHistory && onReloadHistory(); }
        }} className="grid gap-3 md:grid-cols-3">
          <input data-testid="wa-to" type="text" placeholder="(11) 99642-0785" required value={waForm.to} onChange={(e) => setWaForm({ ...waForm, to: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input data-testid="wa-message" type="text" placeholder="Mensagem (dentro da janela 24h)" value={waForm.message} onChange={(e) => setWaForm({ ...waForm, message: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm md:col-span-2" />
          <details className="md:col-span-3 text-xs text-slate-500 -mt-1">
            <summary className="cursor-pointer select-none hover:text-slate-700">Avançado: usar template aprovado (ContentSid)</summary>
            <div className="mt-2">
              <input data-testid="wa-content-sid" type="text" placeholder="HXaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa" value={waForm.content_sid || ""} onChange={(e) => setWaForm({ ...waForm, content_sid: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
              <div className="mt-1 text-[11px] text-slate-500">Cole aqui o Content SID de um template WhatsApp Business aprovado no Twilio (formato <b>HX...</b>). Usado quando o destinatário está fora da janela de 24h. Se em branco, envia mensagem livre.</div>
            </div>
          </details>
          <button type="submit" disabled={waBusy} data-testid="wa-send" className="md:col-span-3 justify-self-start px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <MessageCircle className="w-3.5 h-3.5" /> {waBusy ? "Enviando…" : "Enviar WhatsApp"}
          </button>
        </form>
      )}

      {commsChan === "email" && (
        <form onSubmit={async (e) => {
          e.preventDefault(); setEmailBusy(true);
          try {
            const { data } = await api.post("/admin/email/test", emailForm);
            if (data.ok) toast.success(`Email enviado — ${data.operation_id}`);
            else toast.warning(data.reason || "Não enviado");
          } catch (e2) { toast.error(e2?.response?.data?.detail || "Erro"); }
          finally { setEmailBusy(false); onReloadHistory && onReloadHistory(); }
        }} className="grid gap-3 md:grid-cols-2">
          <input data-testid="email-to" type="email" placeholder="destinatario@exemplo.com" required value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm md:col-span-2" />
          <input data-testid="email-subject" type="text" placeholder="Assunto" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input data-testid="email-html" type="text" placeholder="HTML do corpo (opcional)" value={emailForm.html} onChange={(e) => setEmailForm({ ...emailForm, html: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <button type="submit" disabled={emailBusy} data-testid="email-send" className="md:col-span-2 justify-self-start px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {emailBusy ? "Enviando…" : "Enviar Email"}
          </button>
        </form>
      )}

      <div className="mt-8 pt-6 border-t border-slate-200" data-testid="comm-history-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Últimos envios</div>
            <div className="text-xs text-slate-400">Email · SMS · WhatsApp — histórico das últimas 20 comunicações</div>
          </div>
          <div className="flex items-center gap-2">
            {heatmapFilter && (
              <span data-testid="heatmap-filter-badge" className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#0B2B62] text-white inline-flex items-center gap-2">
                Filtro: {DAYS[heatmapFilter.day]} {String(heatmapFilter.hour).padStart(2,"0")}:00
                <button onClick={onClearHeatmapFilter} data-testid="heatmap-filter-clear"
                  aria-label="Limpar filtro do heatmap"
                  className="text-white/80 hover:text-white">✕</button>
              </span>
            )}
            <button onClick={onReloadHistory} data-testid="comm-history-refresh" className="text-xs px-2.5 py-1 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600">Atualizar</button>
          </div>
        </div>
        {commHistory.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">Nenhum envio registrado ainda.</div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-xs" data-testid="comm-history-table">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 font-semibold">Quando</th>
                  <th className="text-left py-2 px-2 font-semibold">Canal</th>
                  <th className="text-left py-2 px-2 font-semibold">Destinatário</th>
                  <th className="text-left py-2 px-2 font-semibold">Prévia</th>
                  <th className="text-left py-2 px-2 font-semibold">Provedor</th>
                  <th className="text-left py-2 px-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {commHistory.map((c, i) => {
                  const chanColor = c.channel === "email" ? "bg-blue-100 text-blue-800"
                    : c.channel === "whatsapp" ? "bg-green-100 text-green-800"
                    : "bg-slate-100 text-slate-700";
                  const statusColor = c.status === "sent" || c.status === "received" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800";
                  const when = c.at ? new Date(c.at).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : "—";
                  return (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`comm-row-${i}`}>
                      <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{when}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${chanColor}`}>
                          {c.channel}{c.direction === "inbound" && <span className="ml-1">←</span>}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-slate-800 font-mono text-[11px]">{c.to || c.from || "—"}</td>
                      <td className="py-2 px-2 text-slate-600 max-w-[220px] truncate" title={c.preview}>{c.preview || "—"}</td>
                      <td className="py-2 px-2 text-slate-500 text-[11px]">{c.provider || "—"}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${statusColor}`}>{c.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InAppMessagesFeed />
    </div>
  );
};

/**
 * Feed global de chat in-app. Admin filtra por thread + role e clica em
 * qualquer linha para abrir o caso. Isso dá o "controle total das mensagens
 * trocadas entre consultor/família/clínica" pedido pela operação.
 */
const InAppMessagesFeed = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState("");
  const [role, setRole] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (thread) params.thread = thread;
      if (role) params.role = role;
      const { data } = await api.get("/admin/messages", { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) {
      toast.error("Erro ao carregar mensagens in-app");
    } finally { setLoading(false); }
  }, [thread, role]);
  useEffect(() => { load(); }, [load]);

  const Chip = ({ value, current, setter, label }) => (
    <button onClick={() => setter(value === current ? "" : value)}
      className={`text-[11px] px-2 py-0.5 rounded-full border ${value === current ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label}
    </button>
  );

  return (
    <div className="mt-8 pt-6 border-t border-slate-200" data-testid="admin-inapp-messages">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display font-bold text-lg text-[#0B2B62] flex items-center gap-2">
            <MessagesSquare className="w-4 h-4" /> Chat in-app · visão global
          </h3>
          <p className="text-xs text-slate-500">Mensagens trocadas dentro da plataforma entre família, consultor e clínica ({total} no filtro atual).</p>
        </div>
        <button onClick={load} className="text-xs px-2 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold pr-1 py-1">Thread:</span>
        <Chip value="family_consultor" current={thread} setter={setThread} label="Família ↔ Consultor" />
        <Chip value="consultor_clinic" current={thread} setter={setThread} label="Consultor ↔ Clínica" />
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold pl-3 pr-1 py-1">Quem enviou:</span>
        <Chip value="familia" current={role} setter={setRole} label="Família" />
        <Chip value="consultor" current={role} setter={setRole} label="Consultor" />
        <Chip value="clinica" current={role} setter={setRole} label="Clínica" />
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">Sem mensagens nesse filtro.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
          {items.map((m, i) => {
            const when = m.created_at ? new Date(m.created_at).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : "—";
            const roleColor = m.sender_role === "familia" ? "bg-blue-100 text-blue-800"
              : m.sender_role === "consultor" ? "bg-[#0B2B62] text-white"
              : m.sender_role === "clinica" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700";
            return (
              <div key={i} data-testid={`inapp-msg-${i}`}
                className="px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 text-sm">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`inline-flex text-[10px] font-bold uppercase px-2 py-0.5 rounded ${roleColor}`}>{m.sender_role || "?"}</span>
                    <span className="text-xs font-semibold text-slate-800 truncate">{m.sender_name || "—"}</span>
                    <span className="text-[10px] text-slate-400">→ {m.thread_type === "consultor_clinic" ? "Clínica" : "Família"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 shrink-0">
                    <span>{when}</span>
                    {m.case_id && (
                      <Link to={`/consultor/caso/${m.case_id}`} className="text-[#0B2B62] font-semibold hover:underline">
                        {m.protocol || m.case_id.slice(0,8)}
                      </Link>
                    )}
                  </div>
                </div>
                <p className="text-slate-700 leading-snug pl-1 line-clamp-3">{m.content}</p>
                {m.patient_name && (
                  <p className="text-[10px] text-slate-400 mt-0.5 pl-1">Paciente: {m.patient_name}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommsPanel;
